import { ZIP_CODE_VALIDATION_TIMEOUT_MS, ZIP_CODE_VALIDATION_URL } from 'v2/configs/constants'
import type {
  AddBankAccountPayload,
  AddCardForm,
  AddSchedulePayload,
  AutoRechargeLimitForm,
  BillingInformationForm,
  CardCheckoutCompletePayload,
  CardCheckoutSessionPayload,
  CashfreeOrderCreatePayload,
  CashfreePayPayload,
  CashfreeSubscriptionCreatePayload,
  CashfreeVBankCreatePayload,
  DeleteBankAccountPayload,
  DeleteCardPayload,
  RazorpayOrderCreatePayload,
  RazorpayPayWithTokenPayload,
  RazorpayVBankCreatePayload,
  RazorpayVerifyPayload,
  RRazorpayVBank,
  RRazorpayVBankPayments,
  SendBlockedEmailPayload,
  ToggleACHPayload,
  UpdateAuditLogsPayload,
  UpdateBillingAlertsPayload,
  UpdateBillingEmailPayload,
  UpdateDefaultCardPayload,
  UpdateSchedulePayload,
  VerifyBankAccountPayload,
  VerifyGSTNumberPayload,
  VerifyZipCodePayload,
} from 'v2/pages/billing/types'
import { BackendServiceName } from 'v2/types'
import type { BillingService, RechargePayload, RInvoicesPayload } from 'v2/types/api/billing'
import { logger } from 'v2/utils/logger'
import { coreService } from './base-services'
import { createService, extractBlob } from './create-service'

const BILLING_ENDPOINTS = {
  invoices: (payload: RInvoicesPayload) =>
    `invoice?billing_start_date=${payload.start_date}&billing_end_date=${payload.end_date}&limit=${payload.limit}&offset=${payload.offset}&is_zt_comm_invoice=false`,
  currencyInvoices: (payload: RInvoicesPayload) =>
    `currency_invoice?billing_start_date=${payload.start_date}&billing_end_date=${payload.end_date}&limit=${payload.limit}&offset=${payload.offset}&is_zt_comm_invoice=false`,
  balance: (accountId: number) => `/balance/${accountId}/`,
  transactions: (accountId: number, params?: { limit?: number; offset?: number }) =>
    `/accounts/${accountId}/transactions${params?.limit ? `?limit=${params.limit}` : ''}${params?.offset ? `&offset=${params.offset}` : ''}`,
  receiptPDFDetails: (authId: string, uuid: string) => `/payment_receipt/${authId}/${uuid}`,
  cards: (accountId: number) =>
    `/accounts/${accountId}/card?state=active,expired,authentication_required`,
  address: (accountId: number) => `/accounts/${accountId}/billing`,
  rechargeDetails: (accountId: number) => `/accounts/${accountId}/recharge/details`,
  recharge: (data: RechargePayload) => `/recharge/${data.card_mapping_id}`,
  updateDefaultCard: (accountId: number) => `/accounts/${accountId}/card/default`,
  updateCard: (accountId: number) => `/accounts/${accountId}/card`,
  balanceAlerts: (accountId: number) => `/accounts/${accountId}/threshold_alert`,
  billingEmail: (accountId: number) => `/account/${accountId}/billing_email`,
  updateAuditLogs: '/audit',
  getRedirectionUrl: (cardMappingId: number, accountId: number) =>
    `/accounts/${accountId}/card/redirection_url?card_mapping_id=${cardMappingId}`,
  // Embedded card checkout (Stripe PSP-hosted) + Cashfree post-modal status poll.
  cardCheckoutSession: (accountId: number) => `/accounts/${accountId}/card/checkout-session`,
  cardCheckoutComplete: (accountId: number) => `/accounts/${accountId}/card/checkout-complete`,
  cashfreePaymentStatus: (orderId: string) => `/in/payment/${orderId}`,
  accountDetails: (accountId: number) => `/internal/account/id/${accountId}/`,
  verifyGSTNumber: () => `/tasks/sync/verify_with_source/ind_gst_certificate`,
  sendBlockedEmail: () => `/send-blocked-risk-mail`,
  pricing: (countryIso: string) => `/billing/pricing/${countryIso}`,
  smsOutboundRates: () => `/billing/sms-outbound-rates`,
  voiceOutboundRates: () => `/billing/voice-outbound-rates`,
  zentrunkPricing: (countryIso: string) => `/billing/zentrunk-pricing/${countryIso}`,
  // All-country SIP Trunking rate sheets. The per-country zentrunkPricing payload
  // above drives the on-screen card and cannot produce these (Pylon #6431).
  zentrunkOutboundRates: () => `/billing/zentrunk-outbound-rates`,
  zentrunkInboundRates: () => `/billing/zentrunk-inbound-rates`,
  // ACH / Bank Direct Debit
  bankAccount: (accountId: number) =>
    `/accounts/bank?account_id=${accountId}&state=blocked,verification_required,active`,
  bankAccountAdd: '/accounts/bank/add',
  bankAccountDelete: '/accounts/bank/delete',
  bankAccountVerify: '/accounts/bank/verify',
  achStatus: (accountId: number) => `/accounts/${accountId}/bank/auto_debit_schedule`,
  achToggle: (accountId: number) => `/accounts/${accountId}/bank/auto_debit_schedule/toggle`,
  autoDebitSchedules: (accountId: number, bankMappingId: number) =>
    `/accounts/bank/auto_debit_schedule?account_id=${accountId}&bank_mapping_id=${bankMappingId}`,
  autoDebitSchedule: '/accounts/bank/auto_debit_schedule',
  autoDebitScheduleById: (scheduleId: string) => `/accounts/bank/auto_debit_schedule/${scheduleId}`,
  // USD Bank Transfer
  wireFundingInstructions: (accountId: number) =>
    `/accounts/${accountId}/wire_transfer/funding_instructions`,
  // India payment (PSP-agnostic /in/ alias; hodor rewrites to the upstream PSP segment)
  cashfreePaymentVerify: (orderId: string) => `/in/payment/${orderId}/verify`,
  cashfreeVbankCreate: '/in/vbank/create',
  cashfreeVbankPayments: (vbankId: string, limit: number, offset: number) =>
    `/in/vbank/${vbankId}/payments?limit=${limit}&offset=${offset}`,
  cashfreeVbanksByAccount: (accountId: number, limit: number, offset: number) =>
    `/accounts/${accountId}/in/vbanks?limit=${limit}&offset=${offset}`,
  cashfreeCardsList: (accountId: number) => `/accounts/${accountId}/in/cards`,
  cashfreeOrderCreate: '/in/order/create',
  cashfreePay: '/in/pay',
  // Razorpay (India PSP) — plan-B direct /v2/razorpay/* routes. To move to the
  // PSP-agnostic /in/* alias later, only these three strings change.
  razorpayOrderCreate: '/razorpay/orders',
  razorpayPaymentVerify: '/razorpay/payment/verify',
  // Server-to-server charge of a saved (tokenised) card via Razorpay's Native/S2S
  // API. Contract verified against usecases/razorpay_pay_with_token.go.
  razorpayPayWithToken: '/razorpay/pay-with-token',
  razorpayPaymentStatus: (paymentId: string) => `/razorpay/payments/${paymentId}`,
  razorpayCardDelete: (accountId: number, tokenId: string) =>
    `/accounts/${accountId}/razorpay/cards/${tokenId}`,
  razorpayVbankCreate: '/razorpay/vbank/create',
  razorpayVbank: (vbankId: string) => `/razorpay/vbank/${vbankId}`,
  razorpayVbankPayments: (vbankId: string) => `/razorpay/vbank/${vbankId}/payments`,
  cashfreeSubscriptionCreate: '/subscriptions/in',
  cashfreeSubscription: (subscriptionId: string) => `/subscriptions/in/${subscriptionId}`,
  cashfreeSubscriptionPause: (subscriptionId: string) =>
    `/subscriptions/in/${subscriptionId}/pause`,
  cashfreeSubscriptionResume: (subscriptionId: string) =>
    `/subscriptions/in/${subscriptionId}/resume`,
  cashfreeSubscriptionsByAccount: (accountId: number, limit: number, offset: number) =>
    `/accounts/${accountId}/subscriptions/in?limit=${limit}&offset=${offset}`,
}

const NO_CACHE_CONFIG = {
  headers: {
    'Cache-Control': 'no-cache, no-store, must-revalidate',
    Pragma: 'no-cache',
    Expires: '0',
  },
}

const serviceFactory = (): BillingService => {
  const invoiceService = createService('/invoice-service', true, BackendServiceName.billing, 'v2')
  const invoiceServiceV1 = createService('/invoice-service', true, BackendServiceName.billing, 'v1')
  const balanceService = createService('/balance-service', true, BackendServiceName.billing, 'v1')
  const balanceServiceV2 = createService('/balance-service', true, BackendServiceName.billing, 'v2')
  const paymentService = createService('/payment-service', true, BackendServiceName.billing, 'v1')
  const paymentServiceV2 = createService('/payment-service', true, BackendServiceName.billing, 'v2')
  const cxService = createService('/cx', false, BackendServiceName.billing, 'v1')
  const accountManagementService = createService(
    '/ams-service',
    true,
    BackendServiceName.billing,
    'v1',
  )
  const accountService = createService('/account-service', true, BackendServiceName.billing, 'v1')
  const idfyService = createService('/idfy-service', true, BackendServiceName.billing, 'v3')
  return {
    getInvoices: (payload: RInvoicesPayload) =>
      invoiceService.get(BILLING_ENDPOINTS.invoices(payload)),
    getCurrencyInvoices: (payload: RInvoicesPayload) =>
      invoiceServiceV1.get(BILLING_ENDPOINTS.currencyInvoices(payload)),
    getBalance: (accountId: number) =>
      balanceService.get(BILLING_ENDPOINTS.balance(accountId), undefined, NO_CACHE_CONFIG),
    getTransactions: (accountId: number, params?: { limit?: number; offset?: number }) =>
      paymentService.get(
        BILLING_ENDPOINTS.transactions(accountId, params),
        undefined,
        NO_CACHE_CONFIG,
      ),
    getReceiptPDFDetails: (authId: string, uuid: string) =>
      paymentService.get(BILLING_ENDPOINTS.receiptPDFDetails(authId, uuid)),
    getCards: (accountId: number) => paymentService.get(BILLING_ENDPOINTS.cards(accountId)),
    getAddress: (accountId: number) =>
      paymentService.get(BILLING_ENDPOINTS.address(accountId), undefined, NO_CACHE_CONFIG),
    getRechargeDetails: (accountId: number) =>
      paymentService.get(BILLING_ENDPOINTS.rechargeDetails(accountId)),
    recharge: (data: RechargePayload) =>
      paymentServiceV2.post(BILLING_ENDPOINTS.recharge(data), data),
    updateBillingInformation: (data: BillingInformationForm, accountId: number) =>
      paymentService.post(BILLING_ENDPOINTS.address(accountId), data),
    updateDefaultCard: (data: UpdateDefaultCardPayload, accountId: number) =>
      paymentService.put(BILLING_ENDPOINTS.updateDefaultCard(accountId), data),
    deleteCard: (data: DeleteCardPayload, accountId: number) =>
      paymentService.delete(BILLING_ENDPOINTS.updateCard(accountId), data),
    addCard: (data: AddCardForm, accountId: number) =>
      paymentServiceV2.post(BILLING_ENDPOINTS.updateCard(accountId), data),
    updateBillingAlerts: (data: UpdateBillingAlertsPayload, accountId: number) =>
      paymentService.post(BILLING_ENDPOINTS.balanceAlerts(accountId), data),
    getBalanceAlerts: (accountId: number) =>
      paymentService.get(BILLING_ENDPOINTS.balanceAlerts(accountId)),
    updateBillingEmail: (data: UpdateBillingEmailPayload, accountId: number) =>
      accountManagementService.post(BILLING_ENDPOINTS.billingEmail(accountId), data),
    getBillingEmail: (accountId: number) =>
      accountManagementService.get(BILLING_ENDPOINTS.billingEmail(accountId)),
    setupAutoRecharge: (data: AutoRechargeLimitForm, accountId: number) =>
      balanceServiceV2.patch(BILLING_ENDPOINTS.balance(accountId), data),
    updateAuditLogs: (data: UpdateAuditLogsPayload) =>
      accountManagementService.post(BILLING_ENDPOINTS.updateAuditLogs, data),
    getRedirectionUrl: (cardMappingId: number, accountId: number) =>
      paymentServiceV2.get(BILLING_ENDPOINTS.getRedirectionUrl(cardMappingId, accountId)),
    createCardCheckoutSession: (data: CardCheckoutSessionPayload, accountId: number) =>
      paymentServiceV2.post(BILLING_ENDPOINTS.cardCheckoutSession(accountId), data),
    completeCardCheckout: (data: CardCheckoutCompletePayload, accountId: number) =>
      paymentServiceV2.post(BILLING_ENDPOINTS.cardCheckoutComplete(accountId), data),
    cashfreeGetPaymentStatus: (orderId: string) =>
      paymentServiceV2.get(BILLING_ENDPOINTS.cashfreePaymentStatus(orderId)),
    getAccountDetails: (accountId: number) =>
      accountService.get(BILLING_ENDPOINTS.accountDetails(accountId), undefined, NO_CACHE_CONFIG),
    verifyGSTNumber: (data: VerifyGSTNumberPayload) =>
      idfyService.post(BILLING_ENDPOINTS.verifyGSTNumber(), data),
    verifyZipCode: (data: VerifyZipCodePayload) =>
      fetch(`${ZIP_CODE_VALIDATION_URL}/${data.zipcode}`, {
        signal: AbortSignal.timeout(ZIP_CODE_VALIDATION_TIMEOUT_MS),
      })
        .then((res) => {
          if (!res.ok) {
            throw new Error(`HTTP error! status: ${res.status}`)
          }
          return res.json()
        })
        .catch((error) => {
          // The logger JSON.stringifies args, which turns an Error into {}.
          logger.error('Zip code validation failed', {
            error: String(error),
            zipcode: data.zipcode,
          })
          throw error
        }),
    sendBlockedEmail: (data: SendBlockedEmailPayload) =>
      cxService.post(BILLING_ENDPOINTS.sendBlockedEmail(), data),
    getPricing: (countryIso: string) => coreService.get(BILLING_ENDPOINTS.pricing(countryIso)),
    getSmsOutboundRates: () => coreService.get(BILLING_ENDPOINTS.smsOutboundRates()),
    getVoiceOutboundRates: () => coreService.get(BILLING_ENDPOINTS.voiceOutboundRates()),
    getZentrunkOutboundRatesCsv: () =>
      coreService
        .get(BILLING_ENDPOINTS.zentrunkOutboundRates(), undefined, { responseType: 'blob' })
        .then(extractBlob),
    getZentrunkInboundRatesCsv: () =>
      coreService
        .get(BILLING_ENDPOINTS.zentrunkInboundRates(), undefined, { responseType: 'blob' })
        .then(extractBlob),
    getZentrunkPricing: (countryIso: string) =>
      coreService.get(BILLING_ENDPOINTS.zentrunkPricing(countryIso)),
    // ACH / Bank Direct Debit
    getBankAccount: (accountId: number) =>
      paymentServiceV2.get(BILLING_ENDPOINTS.bankAccount(accountId)),
    addBankAccount: (data: AddBankAccountPayload) =>
      paymentServiceV2.post(BILLING_ENDPOINTS.bankAccountAdd, data),
    deleteBankAccount: (data: DeleteBankAccountPayload) =>
      paymentServiceV2.post(BILLING_ENDPOINTS.bankAccountDelete, data),
    verifyBankAccount: (data: VerifyBankAccountPayload) =>
      paymentServiceV2.post(BILLING_ENDPOINTS.bankAccountVerify, data),
    getACHStatus: (accountId: number) =>
      paymentServiceV2.get(BILLING_ENDPOINTS.achStatus(accountId)),
    toggleACH: (accountId: number, data: ToggleACHPayload) =>
      paymentServiceV2.post(BILLING_ENDPOINTS.achToggle(accountId), data),
    getAutoDebitSchedules: (accountId: number, bankMappingId: number) =>
      paymentServiceV2.get(BILLING_ENDPOINTS.autoDebitSchedules(accountId, bankMappingId)),
    addAutoDebitSchedule: (data: AddSchedulePayload) =>
      paymentServiceV2.post(BILLING_ENDPOINTS.autoDebitSchedule, data),
    updateAutoDebitSchedule: (scheduleId: string, data: UpdateSchedulePayload) =>
      paymentServiceV2.put(BILLING_ENDPOINTS.autoDebitScheduleById(scheduleId), data),
    deleteAutoDebitSchedule: (
      scheduleId: string,
      data: { account_id: number; actor: string; client: string },
    ) => paymentServiceV2.delete(BILLING_ENDPOINTS.autoDebitScheduleById(scheduleId), data),
    // USD Bank Transfer
    getWireTransferDetails: (accountId: number) =>
      paymentServiceV2.post(BILLING_ENDPOINTS.wireFundingInstructions(accountId)),
    // Cashfree (India PSP)
    cashfreeCreateOrder: (data: CashfreeOrderCreatePayload) =>
      paymentServiceV2.post(BILLING_ENDPOINTS.cashfreeOrderCreate, data),
    cashfreePay: (data: CashfreePayPayload) =>
      paymentServiceV2.post(BILLING_ENDPOINTS.cashfreePay, data),
    cashfreeVerifyPayment: (orderId: string) =>
      paymentServiceV2.post(BILLING_ENDPOINTS.cashfreePaymentVerify(orderId)),
    cashfreeListCards: (accountId: number) =>
      paymentServiceV2.get(BILLING_ENDPOINTS.cashfreeCardsList(accountId)),
    // Razorpay (India PSP) — routed through hodor via paymentServiceV2, same as
    // the Cashfree endpoints above.
    razorpayCreateOrder: (data: RazorpayOrderCreatePayload) =>
      paymentServiceV2.post(BILLING_ENDPOINTS.razorpayOrderCreate, data),
    razorpayVerifyPayment: (data: RazorpayVerifyPayload) =>
      paymentServiceV2.post(BILLING_ENDPOINTS.razorpayPaymentVerify, data),
    razorpayPayWithToken: (data: RazorpayPayWithTokenPayload) =>
      paymentServiceV2.post(BILLING_ENDPOINTS.razorpayPayWithToken, data),
    // Live payment status straight from Razorpay (FetchPayment), not our
    // payment_log mirror. The 3DS return route uses this to tell success from
    // failure — the answer it cannot read off Razorpay's POSTed callback body.
    getRazorpayPaymentStatus: (paymentId: string) =>
      paymentServiceV2.get(BILLING_ENDPOINTS.razorpayPaymentStatus(paymentId)),
    // Delete a saved Razorpay card by its token_id (the dedicated route also
    // de-tokenises at Razorpay and marks the local mapping removed). The generic
    // /account/card delete can't be used for Razorpay cards — it misroutes the
    // token to Stripe. The token comes from the card's payment_intent_id in the
    // shared /account/card list.
    razorpayDeleteCard: (accountId: number, tokenId: string) =>
      paymentServiceV2.delete(BILLING_ENDPOINTS.razorpayCardDelete(accountId, tokenId)),
    razorpayCreateVbank: (data: RazorpayVBankCreatePayload) =>
      paymentServiceV2.post<RRazorpayVBank>(BILLING_ENDPOINTS.razorpayVbankCreate, data),
    razorpayGetVbank: (vbankId: string) =>
      paymentServiceV2.get<RRazorpayVBank>(BILLING_ENDPOINTS.razorpayVbank(vbankId)),
    razorpayGetVbankPayments: (vbankId: string) =>
      paymentServiceV2.get<RRazorpayVBankPayments>(
        BILLING_ENDPOINTS.razorpayVbankPayments(vbankId),
      ),
    cashfreeCreateVbank: (data: CashfreeVBankCreatePayload) =>
      paymentServiceV2.post(BILLING_ENDPOINTS.cashfreeVbankCreate, data),
    cashfreeGetVbankPayments: (vbankId: string, limit = 20, offset = 0) =>
      paymentServiceV2.get(BILLING_ENDPOINTS.cashfreeVbankPayments(vbankId, limit, offset)),
    cashfreeListVbanks: (accountId: number, limit = 10, offset = 0) =>
      paymentServiceV2.get(BILLING_ENDPOINTS.cashfreeVbanksByAccount(accountId, limit, offset)),
    cashfreeCreateSubscription: (data: CashfreeSubscriptionCreatePayload) =>
      paymentServiceV2.post(BILLING_ENDPOINTS.cashfreeSubscriptionCreate, data),
    cashfreeCancelSubscription: (subscriptionId: string) =>
      paymentServiceV2.delete(BILLING_ENDPOINTS.cashfreeSubscription(subscriptionId)),
    cashfreePauseSubscription: (subscriptionId: string) =>
      paymentServiceV2.post(BILLING_ENDPOINTS.cashfreeSubscriptionPause(subscriptionId)),
    cashfreeResumeSubscription: (subscriptionId: string) =>
      paymentServiceV2.post(BILLING_ENDPOINTS.cashfreeSubscriptionResume(subscriptionId)),
    cashfreeListSubscriptions: (accountId: number, limit = 10, offset = 0) =>
      paymentServiceV2.get(
        BILLING_ENDPOINTS.cashfreeSubscriptionsByAccount(accountId, limit, offset),
      ),
  }
}

export const billingService = serviceFactory()
